import type { CertificationProject } from "../types/generated-graphql";
import { openstackProjectTypes, projectContainerTypes, projectTypes } from "./projectTypes";

export const ProjectContainer = {
	autoPublish: "auto_publish",
	autoRebuild: "auto-rebuild",
	applicationCategories: "application_categories",
	distributionApproval: "distribution_approval",
	distributionMethod: "distribution_method",
	isvPID: "isv_pid",
	kubeObjects: "kube_objects",
	osContentType: "os_content_type",
	passedRHCert: "passed_rhcert",
	privileged: "privileged",
	published: "published",
	registry: "registry",
	registryOverrideInstruct: "registry_override_instruct",
	registryVendor: "registry_vendor",
	releaseCategory: "release_category",
	repository: "repository",
	repositoryName: "repository_name",
	repositoryDescription: "repository_description",
	serviceAccountSecret: "service_account_secret",
	supportPlatforms: "support_platforms",
	shortDescription: "short_description",
	type: "type",
} as const;

export enum EOSContentTypes {
	OperatorBundleImage = "Operator Bundle Image",
	UBI = "Red Hat Universal Base Image (UBI)",
	RHEL = "Red Hat Enterprise Linux",
}

export const ProjectKeys = {
	id: "_id",
	certificationDate: "certification_date",
	certificationStatus: "certification_status",
	certificationLevel: "certification_level",
	badges: "badges",
	certificationUser: "certification_user",
	container: "container",
	creationDate: "creation_date",
	drupal: "drupal",
	selected: "selected",
	helmChart: "helm_chart",
	lastUpdateDate: "last_update_date",
	legacyProductID: "legacy_product_id",
	projectName: "name",
	mappedProductListings: "mapped_product_listings",
	operatorDistribution: "operator_distribution",
	productListings: "product_listings",
	projectStatus: "project_status",
	publishedBy: "published_by",
	redhat: "redhat",
	selfCertification: "self_certification",
	contacts: "contacts",
	type: "type",
	catalogVisibility: "catalog_visibility",
} as const;

export enum ECatalogVisibility {
	visible = "visible",
	hidden = "hidden",
}

export const ProjectHelmChartKeys = {
	distributionMethod: "distribution_method",
	chartName: "chart_name",
	repository: "repository",
	distributionInstructions: "distribution_instructions",
	publicPgpKey: "public_pgp_key",
	githubUsernames: "github_usernames",
	githubPullRequest: "github_pull_request",
	shortDescription: "short_description",
	longDescription: "long_description",
	applicationCategories: "application_categories",
} as const;

export const ProjectContainerTypes = {
	container: "container",
	operator: "operator bundle image",
	scratch: "scratch",
};

export enum ProjectTypes {
	containers = "Containers",
	rhel = "RHEL",
	helmChart = "Helm Chart",
	openStackInfraNonContainer = "OpenStack-infra-noncontainer",
	openStackInfraContainer = "OpenStack-infra-container",
	openstackVNF = "OpenStack-vnf",
	openstackAppContainer = `OpenStack-app-container`,
	openShiftCnf = "OpenShift-cnf",
	openShiftVirtualization = "OpenShift-virtualization",
}

export const ProjectRedhatKeys = {
	productID: "product_id",
	productName: "product_name",
	productVersion: "product_version",
	productVersionID: "product_version_id",
} as const;

// project.type = Containers then show Project type as container.type in Project List
export const getProjectTypeData = (data: CertificationProject) => {
	if (data?.type === "Containers") {
		return projectContainerTypes.find((el) => el.value === data?.container?.type)?.text || "";
	} else if (data?.type === "RHEL") {
		const rhelVersion = data?.redhat?.product_version || null;
		return rhelVersion
			? `Red Hat Enterprise Linux ${data?.redhat?.product_version} Application`
			: projectTypes.find((el) => el.value === data?.type)?.text || "";
	} else if (data?.type === "Middleware") {
		// Depreciated type just for the table on archived mode
		return "Red Hat Middleware";
	} else {
		return (
			projectTypes.find((el) => el.value === data?.type)?.text ||
			openstackProjectTypes.find((el) => el.value === data?.type)?.text ||
			""
		);
	}
};

export const DepreciatedProjectTypes = ["Middleware", ProjectTypes.openShiftVirtualization];
